// src/components/AddEmployee.js

/**
 * AddEmployee Component - Multi-step form for adding/editing employees
 * Updated with 4-digit employee ID system (0001-9999)
 * Following the established CSS architecture and component patterns
 */

import React, { useState, useEffect, useCallback } from "react";
import Icon from "./icons/Icon";
import { useNavigate, useParams } from "react-router-dom";
import {
  doc,
  collection,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../hooks/useAuth";

// Import step components
import { StepPersonalInfo } from "./employee-steps/StepPersonalInfo";
import { StepEmployment } from "./employee-steps/StepEmployment";
import { StepSensitiveInfo } from "./employee-steps/StepSensitiveInfo";
import { StepReview } from "./employee-steps/StepReview";
import ConfirmModal from "./modals/ConfirmModal";
import ResetKioskPinModal from "./settings/ResetKioskPinModal";

// Import services for audit + employee writes
import employeeAuditService from "../services/employeeAuditService";
import { createEmployee, saveEmployee, isEmployeeIdTaken } from "../services/employeeService";

// Import styles - no inline CSS
import "../styles/main.css";

export const AddEmployee = () => {
  const navigate = useNavigate();
  const { user, company, hasPermission, loading: authLoading } = useAuth();
  const { slug, employeeId: currentEmployeeId } = useParams();

  // Sensitive Info step is gated on emp:pii:edit. Without the permission,
  // the wizard collapses to 3 steps (Personal Info → Employment → Review)
  // and the new employee is created with hasPii: false. PRD §6.1.
  const hasSensitiveAccess = hasPermission("emp:pii:edit");

  // Step definitions — order is meaningful (currentStep is 1-indexed
  // into this array). The Sensitive Info entry is conditionally included.
  //
  // BL-063: Sensitive Info (which holds Compensation/salary) runs BEFORE
  // Employment (which holds the government-contribution inputs) so the
  // salary-derived SSS suggestion chip in Employment can read an already-entered
  // salary. Without PII access there is no salary step, so the chip is simply
  // absent (manual entry) and the 3-step flow is unchanged.
  const wizardSteps = hasSensitiveAccess
    ? ["personal", "sensitive", "employment", "review"]
    : ["personal", "employment", "review"];
  const totalSteps = wizardSteps.length;
  const stepLabels = {
    personal: "Personal Info",
    employment: "Employment",
    sensitive: "Sensitive Info",
    review: "Review",
  };

  // Edit mode detection
  const isEdit = Boolean(currentEmployeeId);
  const [showPinModal, setShowPinModal] = useState(false);

  // State Management
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  // PRD_CbaPremiumBase C12(a). `null` = unknown (not yet loaded, or the read
  // failed) and deliberately does NOT block the switch — C12(b) refuses to pay
  // such an employee at payroll time, and a failed settings read must not stop
  // an operator saving an employee. StepSensitiveInfo is presentational and
  // reads no Firestore, so the fetch lives here, the only mount point for both
  // the add and the edit routes.
  const [companyMinimumDailyWage, setCompanyMinimumDailyWage] = useState(null);
  // H11: agencies whose basis this company composes, for StepReview's note.
  const [basisAgencies, setBasisAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [employeeIdType, setEmployeeIdType] = useState("auto");
  const [nextEmployeeId, setNextEmployeeId] = useState("0001");
  const [employeeDocId, setEmployeeDocId] = useState(null);
  const [scheduleDefaults] = useState({ startTime: "08:00", endTime: "17:00" });
  // Tracks user-driven form changes. Flipped true only inside
  // handleInputChange — direct setFormData calls during loadEmployeeData
  // (edit mode) intentionally don't mark the form dirty.
  const [isDirty, setIsDirty] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showClearEndDateConfirm, setShowClearEndDateConfirm] = useState(false);

  // Form data structure
  const [formData, setFormData] = useState({
    employeeId: "",
    personalInfo: {
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      mobile: "",
      dateOfBirth: "",
      gender: "",
      maritalStatus: "",
    },
    employment: {
      department: "",
      startDate: "",
      employmentType: "regular",
      salaryType: "daily",
      // M4 (PRD_MonthlySalaryType): monthly-only pay-basis toggle, default
      // 'fixed' — attendance affects pay only when explicitly switched On.
      payBasis: "fixed",
      monthlySalary: "",
      dailySalary: "",
      sssContribution: "",
      pagibigContribution: "",
      philhealthContribution: "",
      withholdingTax: "",
      inBankExportFile: true,
      workSchedule: {
        sunday: {
          isWorkingDay: false,
          startTime: "08:00",
          endTime: "17:00",
          breakDuration: 60,
        },
        monday: {
          isWorkingDay: true,
          startTime: "08:00",
          endTime: "17:00",
          breakDuration: 60,
        },
        tuesday: {
          isWorkingDay: true,
          startTime: "08:00",
          endTime: "17:00",
          breakDuration: 60,
        },
        wednesday: {
          isWorkingDay: true,
          startTime: "08:00",
          endTime: "17:00",
          breakDuration: 60,
        },
        thursday: {
          isWorkingDay: true,
          startTime: "08:00",
          endTime: "17:00",
          breakDuration: 60,
        },
        friday: {
          isWorkingDay: true,
          startTime: "08:00",
          endTime: "17:00",
          breakDuration: 60,
        },
        saturday: {
          isWorkingDay: false,
          startTime: "08:00",
          endTime: "17:00",
          breakDuration: 60,
        },
      },
    },
    governmentIds: {
      tin: "",
      sss: "",
      philhealth: "",
      pagibig: "",
    },
    banking: {
      accountNumber: "",
      accountName: "",
    },
  });

  // PRD_CbaPremiumBase C12(a): the company's saved minimum daily wage, for the
  // Sensitive Info switch. Its own effect with its own catch — on failure the
  // value stays `null` (unknown), which does not block the switch.
  useEffect(() => {
    let cancelled = false;
    if (!company?.id) return undefined;
    getDoc(doc(db, "companies", company.id, "settings", "payrollRates"))
      .then((snap) => {
        if (cancelled) return;
        const wage = snap.exists() ? snap.data().minimumDailyWage : 0;
        setCompanyMinimumDailyWage(Number(wage) || 0);
        // PRD_PhilHealthContributionBasis H11: StepReview has no useAuth and no
        // read of its own, so the basis flags ride this snapshot rather than a
        // second getDoc of the same document.
        const rates = snap.exists() ? snap.data() : {};
        setBasisAgencies(
          [
            rates.sssBasis?.enabled === true && "SSS",
            rates.philhealthBasis?.enabled === true && "PhilHealth",
          ].filter(Boolean)
        );
      })
      .catch((err) => {
        console.error("Error loading minimum daily wage:", err);
        if (!cancelled) setCompanyMinimumDailyWage(null);
        // H11: a failed read leaves basisAgencies empty — no note is better
        // than a wrong one.
      });
    return () => {
      cancelled = true;
    };
  }, [company?.id]);

  /**
   * Generate numeric employee ID (4-digit format: 0001, 0002, etc.)
   * Scoped per company - each company can have its own 0001
   */
  const generateEmployeeId = async () => {
    try {
      if (!company?.id) {
        console.error("No company ID available");
        return;
      }

      // Query employees from employees subcollection
      const employeesRef = collection(db, "companies", company.id, "employees");
      const q = query(employeesRef, where("role", "==", "employee"));
      const snapshot = await getDocs(q);

      let highestId = 0;

      // Find the highest numeric ID for this company
      snapshot.forEach((doc) => {
        const employeeData = doc.data();
        const employeeId = employeeData.employeeId;

        // Check if it's a pure numeric ID (4 digits)
        if (employeeId && /^\d{4}$/.test(employeeId)) {
          const numericId = parseInt(employeeId, 10);
          if (numericId > highestId) {
            highestId = numericId;
          }
        }
      });

      // At the 4-digit ceiling, fall through to gap-fill (lowest unused ID)
      // instead of hard-blocking — a single high/stray ID (e.g. a manual 9999)
      // must not lock out auto-generation. Only a genuinely full 0001-9999
      // range errors out.
      let nextId;
      if (highestId >= 9999) {
        const gapId = await findGapInSequence(company.id);
        if (!gapId) {
          setErrors({
            employeeId:
              "Maximum employee limit reached. Please contact support.",
          });
          return;
        }
        nextId = gapId;
      } else {
        nextId = (highestId + 1).toString().padStart(4, "0");
      }

      setFormData((prev) => ({
        ...prev,
        employeeId: nextId,
      }));

      // Also update the preview
      setNextEmployeeId(nextId);
    } catch (error) {
      console.error("Error generating employee ID:", error);

      // Fallback for unexpected errors (e.g. a failed query): try to find a gap
      // in the sequence. The 9999 ceiling is handled inline above, not here.
      try {
        const gapId = await findGapInSequence(company.id);
        if (gapId) {
          setFormData((prev) => ({
            ...prev,
            employeeId: gapId,
          }));
          setNextEmployeeId(gapId);
        } else {
          // Ultimate fallback - notify user
          setErrors({
            employeeId:
              "Unable to generate employee ID. Please enter manually.",
          });
          setEmployeeIdType("manual");
        }
      } catch (fallbackError) {
        console.error("Fallback ID generation failed:", fallbackError);
        setErrors({
          employeeId: "Unable to generate employee ID. Please enter manually.",
        });
        setEmployeeIdType("manual");
      }
    }
  };

  /**
   * Find the lowest unused 4-digit ID — the ceiling fallback for auto-gen.
   * Status-blind on purpose: scan EVERY employee doc (no isDeleted filter) so a
   * gap is only an ID that never existed. Reusing a resigned/terminated (or, if
   * a delete feature ever ships, deleted) employee's ID would re-entangle their
   * leftover DTRs — the exact bug this change prevents.
   */
  const findGapInSequence = async (companyId) => {
    try {
      const employeesRef = collection(db, "companies", companyId, "employees");
      const q = query(employeesRef, where("role", "==", "employee"));
      const snapshot = await getDocs(q);

      // Collect all existing IDs
      const existingIds = new Set();
      snapshot.forEach((doc) => {
        const employeeId = doc.data().employeeId;
        if (employeeId && /^\d{4}$/.test(employeeId)) {
          existingIds.add(employeeId);
        }
      });

      // Find first gap in sequence
      for (let i = 1; i <= 9999; i++) {
        const candidateId = i.toString().padStart(4, "0");
        if (!existingIds.has(candidateId)) {
          return candidateId;
        }
      }

      return null; // No gaps found
    } catch (error) {
      console.error("Error finding gap in sequence:", error);
      return null;
    }
  };

  /**
   * Get the next employee ID for preview (similar to generateEmployeeId but doesn't set state)
   */
  const getNextEmployeeId = async () => {
    try {
      if (!company?.id) return "0001";

      const employeesRef = collection(db, "companies", company.id, "employees");
      const q = query(employeesRef, where("role", "==", "employee"));
      const snapshot = await getDocs(q);

      let highestId = 0;

      snapshot.forEach((doc) => {
        const employeeData = doc.data();
        const employeeId = employeeData.employeeId;

        if (employeeId && /^\d{4}$/.test(employeeId)) {
          const numericId = parseInt(employeeId, 10);
          if (numericId > highestId) {
            highestId = numericId;
          }
        }
      });

      // At the ceiling, preview the lowest unused ID (gap-fill) rather than
      // "MAX", mirroring generateEmployeeId so the preview never dead-ends.
      if (highestId >= 9999) {
        const gapId = await findGapInSequence(company.id);
        return gapId || "MAX";
      }
      return (highestId + 1).toString().padStart(4, "0");
    } catch (error) {
      console.error("Error getting next employee ID:", error);
      return "0001";
    }
  };

  /**
   * Manual-entry uniqueness check (Step 1 early feedback). Returns true when no
   * employee in this company already holds `id`. The authoritative guard lives
   * in employeeService.createEmployee; this is best-effort UX.
   */
  const checkEmployeeIdAvailable = useCallback(
    async (id) => {
      if (!company?.id || !/^\d{4}$/.test(id)) return true;
      return !(await isEmployeeIdTaken(company.id, id));
    },
    [company?.id]
  );

  /**
   * Initialize component - load employee data if editing
   */
  useEffect(() => {
    if (isEdit && currentEmployeeId && company?.id) {
      loadEmployeeData();
    } else if (!isEdit && company?.id) {
      // Get next employee ID for preview
      getNextEmployeeId().then((id) => {
        setNextEmployeeId(id);
        if (employeeIdType === "auto") {
          setFormData((prev) => ({
            ...prev,
            employeeId: id,
          }));
        }
      });

      // Generate employee ID if auto mode
      if (employeeIdType === "auto") {
        generateEmployeeId();
      }
    }
  }, [company, isEdit, employeeIdType]);


  /**
   * Helper function to preserve zero values when loading form data
   * Converts numeric values to strings for form inputs while preserving zeros
   */
  const getValueOrEmpty = (value) => {
    return value !== undefined && value !== null ? String(value) : "";
  };

  /**
   * Load employee data for editing
   */
  const loadEmployeeData = async () => {
    try {
      setLoading(true);

      // Query from employees subcollection
      const employeesRef = collection(db, "companies", company.id, "employees");
      const q = query(
        employeesRef,
        where("role", "==", "employee"),
        where("employeeId", "==", currentEmployeeId)
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const docId = snapshot.docs[0].id;
        const basicDoc = snapshot.docs[0].data();
        setEmployeeDocId(docId);

        // v2.0: also fetch the PII subcollection and merge fields back
        // into the form state at their legacy paths (banking.*,
        // governmentIds.*, employment.salaryType / dailySalary /
        // monthlySalary). For pre-migration legacy employees the PII
        // doc doesn't exist yet — the merge falls back to the parent
        // doc's fields, which still hold the data in that case.
        //
        // Gated on emp:pii:view because Firestore rules deny the read
        // for callers without it; attempting the fetch unconditionally
        // would throw and break the entire load. Custom roles like
        // Manage Employee = Edit + Sensitive Info = None hit this path
        // — they edit basic fields without seeing PII.
        let piiData = null;
        if (hasSensitiveAccess || hasPermission("emp:pii:view")) {
          const piiSnap = await getDoc(
            doc(employeesRef, docId, "pii", docId),
          );
          piiData = piiSnap.exists() ? piiSnap.data() : null;
        }

        const data = piiData
          ? {
              ...basicDoc,
              banking: piiData.banking || basicDoc.banking,
              governmentIds: piiData.governmentIds || basicDoc.governmentIds,
              employment: {
                ...basicDoc.employment,
                ...(piiData.compensation || {}),
              },
            }
          : basicDoc;
        setFormData({
          employeeId: data.employeeId || "",
          personalInfo: {
            firstName: data.personalInfo?.firstName || "",
            middleName: data.personalInfo?.middleName || "",
            lastName: data.personalInfo?.lastName || "",
            email: data.personalInfo?.email || "",
            mobile: data.personalInfo?.mobile || "",
            dateOfBirth: data.personalInfo?.dateOfBirth || "",
            gender: data.personalInfo?.gender || "",
            maritalStatus: data.personalInfo?.maritalStatus || "",
          },
          employment: {
            department: data.employment?.department || "",
            startDate: data.employment?.startDate || "",
            employmentType: data.employment?.employmentType || "regular",
            salaryType: data.employment?.salaryType || "daily",
            payBasis: data.employment?.payBasis || "fixed",
            // PRD_CbaPremiumBase C3. Reads the LOADED doc (`data`), not
            // `formData` — this is the hydrate path, where formData still holds
            // the previous state and would never carry the stored flag.
            cbaPremiumBase: data.employment?.cbaPremiumBase === true,
            monthlySalary: getValueOrEmpty(data.employment?.monthlySalary) || getValueOrEmpty(data.employment?.basicSalary),
            dailySalary: getValueOrEmpty(data.employment?.dailySalary),
            sssContribution: getValueOrEmpty(data.employment?.sssContribution),
            pagibigContribution: getValueOrEmpty(data.employment?.pagibigContribution),
            philhealthContribution: getValueOrEmpty(data.employment?.philhealthContribution),
            withholdingTax: getValueOrEmpty(data.employment?.withholdingTax),
            inBankExportFile: data.employment?.inBankExportFile !== undefined ? data.employment.inBankExportFile : true,
            endDate: data.employment?.endDate || "",
            endReason: data.employment?.endReason || "",
            notes: data.employment?.notes || "",
            workSchedule: data.employment?.workSchedule || {
              sunday: {
                isWorkingDay: false,
                startTime: "08:00",
                endTime: "17:00",
                breakDuration: 60,
              },
              monday: {
                isWorkingDay: true,
                startTime: "08:00",
                endTime: "17:00",
                breakDuration: 60,
              },
              tuesday: {
                isWorkingDay: true,
                startTime: "08:00",
                endTime: "17:00",
                breakDuration: 60,
              },
              wednesday: {
                isWorkingDay: true,
                startTime: "08:00",
                endTime: "17:00",
                breakDuration: 60,
              },
              thursday: {
                isWorkingDay: true,
                startTime: "08:00",
                endTime: "17:00",
                breakDuration: 60,
              },
              friday: {
                isWorkingDay: true,
                startTime: "08:00",
                endTime: "17:00",
                breakDuration: 60,
              },
              saturday: {
                isWorkingDay: false,
                startTime: "08:00",
                endTime: "17:00",
                breakDuration: 60,
              },
            },
          },
          governmentIds: {
            tin: data.governmentIds?.tin || "",
            sss: data.governmentIds?.sss || "",
            philhealth: data.governmentIds?.philhealth || "",
            pagibig: data.governmentIds?.pagibig || "",
          },
          banking: {
            accountNumber: data.banking?.accountNumber || "",
            accountName: data.banking?.accountName || "",
          },
        });
      } else {
        throw new Error("Employee not found");
      }
    } catch (error) {
      console.error("Error loading employee data:", error);
      setErrors({
        submit: "Failed to load employee data. Please try again.",
      });
      navigate(`/company/${company.slug}/employees`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle input changes - matching your step component expectations
   */
  const handleInputChange = (section, field, value) => {
    // Preserve safety check for undefined values
    if (value === undefined || value === null) {
      console.warn(
        `Attempted to set undefined/null value for ${section}.${field}`
      );
      return;
    }

    if (section === "root") {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    }

    setIsDirty(true);

    // Clear errors for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  /**
   * Validate current step. Maps the 1-based step number to its key via
   * wizardSteps, then runs the matching validation block.
   */
  const validateStep = (step) => {
    const stepErrors = {};
    const stepKey = wizardSteps[step - 1];

    switch (stepKey) {
      case "personal":
        // Validate Personal Info
        if (employeeIdType === "manual" && !formData.employeeId?.trim()) {
          stepErrors.employeeId = "Employee ID is required";
        } else if (
          employeeIdType === "manual" &&
          !/^\d{4}$/.test(formData.employeeId)
        ) {
          stepErrors.employeeId = "Employee ID must be exactly 4 digits";
        }
        if (!formData.personalInfo.firstName?.trim()) {
          stepErrors.firstName = "First name is required";
        }
        if (!formData.personalInfo.lastName?.trim()) {
          stepErrors.lastName = "Last name is required";
        }
        if (
          formData.personalInfo.email?.trim() &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personalInfo.email)
        ) {
          stepErrors.email = "Invalid email format";
        }
        if (
          formData.personalInfo.mobile?.trim() &&
          !/^09\d{9}$/.test(formData.personalInfo.mobile)
        ) {
          stepErrors.mobile = "Mobile must be in format 09XXXXXXXXX";
        }
        break;

      case "employment":
        // Validate Employment (no salary or banking — those moved to
        // Sensitive Info step).
        if (!formData.employment.startDate) {
          stepErrors.startDate = "Start date is required";
        }

        // Validate government contributions (required, can be 0 but not empty)
        if (formData.employment.sssContribution === undefined ||
            formData.employment.sssContribution === '') {
          stepErrors.sssContribution = "SSS contribution is required";
        } else if (parseFloat(formData.employment.sssContribution) < 0) {
          stepErrors.sssContribution = "SSS contribution cannot be negative";
        }

        if (formData.employment.pagibigContribution === undefined ||
            formData.employment.pagibigContribution === '') {
          stepErrors.pagibigContribution = "Pag-IBIG contribution is required";
        } else if (parseFloat(formData.employment.pagibigContribution) < 0) {
          stepErrors.pagibigContribution = "Pag-IBIG contribution cannot be negative";
        }

        if (formData.employment.philhealthContribution === undefined ||
            formData.employment.philhealthContribution === '') {
          stepErrors.philhealthContribution = "PhilHealth contribution is required";
        } else if (parseFloat(formData.employment.philhealthContribution) < 0) {
          stepErrors.philhealthContribution = "PhilHealth contribution cannot be negative";
        }

        if (formData.employment.withholdingTax === undefined ||
            formData.employment.withholdingTax === '') {
          stepErrors.withholdingTax = "Withholding tax is required";
        } else if (parseFloat(formData.employment.withholdingTax) < 0) {
          stepErrors.withholdingTax = "Withholding tax cannot be negative";
        }

        // End date validation
        if (isEdit && formData.employment.endDate) {
          const endDate = new Date(formData.employment.endDate);
          const startDate = new Date(formData.employment.startDate);

          if (endDate < startDate) {
            stepErrors.endDate = "End date cannot be before start date";
          }
        }

        // Validate work schedule (at least one working day required)
        const workSchedule = formData.employment.workSchedule;
        if (workSchedule) {
          const hasWorkingDay = Object.values(workSchedule).some(day => day?.isWorkingDay);
          if (!hasWorkingDay) {
            stepErrors.workSchedule = "At least one working day must be selected";
          }
        }
        break;

      case "sensitive":
        // Sensitive Info validation. The step component's button gate
        // (isStepComplete inside StepSensitiveInfo) already enforces
        // the format checks below as a UI guard, so this is belt +
        // suspenders for the wizard's handleNext path.
        if (formData.employment?.salaryType === "monthly") {
          if (
            !formData.employment?.monthlySalary ||
            parseFloat(formData.employment.monthlySalary) <= 0
          ) {
            stepErrors.salary = "Monthly salary is required";
          }
        } else if (
          !formData.employment?.dailySalary ||
          parseFloat(formData.employment.dailySalary) <= 0
        ) {
          stepErrors.salary = "Daily salary is required";
        }
        if (!formData.banking?.accountNumber?.trim()) {
          stepErrors.accountNumber = "Account number is required";
        }
        if (
          formData.governmentIds?.tin &&
          !/^\d{12}$/.test(formData.governmentIds.tin.replace(/-/g, ""))
        ) {
          stepErrors.tin = "Invalid TIN format";
        }
        if (
          formData.governmentIds?.sss &&
          !/^\d{10}$/.test(formData.governmentIds.sss.replace(/-/g, ""))
        ) {
          stepErrors.sss = "Invalid SSS format";
        }
        if (
          formData.governmentIds?.philhealth &&
          !/^\d{12}$/.test(formData.governmentIds.philhealth.replace(/-/g, ""))
        ) {
          stepErrors.philhealth = "Invalid PhilHealth format";
        }
        if (
          formData.governmentIds?.pagibig &&
          !/^\d{12}$/.test(formData.governmentIds.pagibig.replace(/-/g, ""))
        ) {
          stepErrors.pagibig = "Invalid Pag-IBIG format";
        }
        break;

      case "review":
        // Read-only summary — no validation needed.
        break;

      default:
        break;
    }

    return stepErrors;
  };

  /**
   * Handle navigation between steps
   */
  const handleNext = () => {
    const stepErrors = validateStep(currentStep);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      setErrors({});
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowCancelConfirm(true);
      return;
    }
    navigate(`/company/${company.slug}/employees`);
  };

  const confirmDiscardAndLeave = () => {
    setShowCancelConfirm(false);
    navigate(`/company/${company.slug}/employees`);
  };

  /**
   * Clear the employment end date (BL-075) — the "un-end this employee" action.
   *
   * All three fields reset together on purpose: `endReason` sits behind a control
   * that disables itself without a date, and `notes` only renders while a date is
   * set, so leaving either behind strands data the form can no longer show or edit.
   *
   * Deliberately does NOT touch status.current. Active/Inactive is derived from the
   * employment dates everywhere it is displayed (isEmployeeActiveToday), so clearing
   * the date is the whole change — the employee reads Active immediately.
   */
  const clearEndDate = () => {
    handleInputChange("employment", "endDate", "");
    handleInputChange("employment", "endReason", "");
    handleInputChange("employment", "notes", "");
    setShowClearEndDateConfirm(false);
  };

  /**
   * Save employee data - Using atomic transaction for consistency
   */
  const handleSubmit = async () => {
    // Validate final step
    const stepErrors = validateStep(currentStep);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    try {
      setSaving(true);

      if (!company?.id) {
        throw new Error("No company ID available");
      }

      if (isEdit) {
        // Handle update case
        await handleUpdateEmployee();
      } else {
        // Handle new employee case with atomic transaction
        await handleCreateEmployee();
      }

      // Save succeeded — clear dirty flag so post-save navigation skips the
      // "discard unsaved changes?" confirm.
      setIsDirty(false);

      // Success - navigate back to employee list with success message
      navigate(`/company/${company.slug}/employees`, {
        state: {
          message: isEdit
            ? `Employee ${formData.employeeId} updated successfully!`
            : `Employee ${formData.employeeId} created successfully!`,
          type: "success",
        },
      });
    } catch (error) {
      console.error("Error saving employee:", error);
      if (error?.code === "duplicate-employee-id") {
        if (employeeIdType === "auto") {
          // A concurrent create (or gap-fill) took this auto ID. Regenerate so
          // the stale formData.employeeId doesn't re-collide on the next Save.
          await generateEmployeeId();
          setErrors({
            submit: `Employee ID ${error.employeeId} was just taken. A new ID has been assigned — please review and Save again.`,
          });
        } else {
          setErrors({
            submit: `Employee ID ${error.employeeId} is already in use. For a re-hire, switch to Auto-Generate to get a new ID.`,
          });
        }
      } else {
        setErrors({
          submit: `Failed to save employee: ${error.message}`,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle creating a new employee with atomic transaction
   */
  const handleCreateEmployee = async () => {
    // 1. Generate employee ID if needed (auto mode only)
    let finalEmployeeId = formData.employeeId;

    if (employeeIdType === "auto" && !finalEmployeeId) {
      const employeesRef = collection(db, "companies", company.id, "employees");
      const q = query(employeesRef, where("role", "==", "employee"));
      const snapshot = await getDocs(q);

      let highestNum = 0;
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.employeeId) {
          const num = parseInt(data.employeeId);
          if (!isNaN(num) && num > highestNum) {
            highestNum = num;
          }
        }
      });

      // Ceiling-safe: at 9999, gap-fill instead of emitting a malformed
      // 5-digit "10000". A genuinely full range throws (surfaced by the
      // handleSubmit catch).
      if (highestNum >= 9999) {
        finalEmployeeId = await findGapInSequence(company.id);
        if (!finalEmployeeId) {
          throw new Error(
            "Maximum employee limit reached for this company (9999)"
          );
        }
      } else {
        finalEmployeeId = (highestNum + 1).toString().padStart(4, "0");
      }
    }

    // 2. Normalize compensation values (round to 2 decimal places).
    // Contribution fields are written to parent doc regardless of PII
    // access. Salary/banking/govIds only when the creator has emp:pii:edit.
    const contributions = {
      sssContribution: Math.round((parseFloat(formData.employment.sssContribution) || 0) * 100) / 100,
      pagibigContribution: Math.round((parseFloat(formData.employment.pagibigContribution) || 0) * 100) / 100,
      philhealthContribution: Math.round((parseFloat(formData.employment.philhealthContribution) || 0) * 100) / 100,
      withholdingTax: Math.round((parseFloat(formData.employment.withholdingTax) || 0) * 100) / 100,
    };
    const piiCompensation = hasSensitiveAccess
      ? {
          salaryType: formData.employment.salaryType || 'daily',
          dailySalary: Math.round((parseFloat(formData.employment.dailySalary) || 0) * 100) / 100,
          monthlySalary: Math.round((parseFloat(formData.employment.monthlySalary) || 0) * 100) / 100,
          // PRD_CbaPremiumBase C3 — rides the SAME PII-compensation path as the
          // salary it re-bases. `=== true` normalizes to a real boolean: this
          // object reaches Firestore, and an `undefined` would throw (the
          // project does not set ignoreUndefinedProperties).
          cbaPremiumBase: formData.employment.cbaPremiumBase === true,
        }
      : {};

    // Future-dated hires carry real compensation from creation. Payroll filters
    // by startDate on the server so they aren't paid before they start,
    // and Active/Inactive is derived from the dates — no stored status to set.

    // 3. Build the full flat employee shape — employeeService.createEmployee
    // splits this into basic + PII docs internally. When the caller lacks
    // emp:pii:edit, banking/governmentIds/salary fields are omitted entirely
    // so no PII subcollection write is attempted (which would be denied
    // by the rule). Result: hasPii: false on the basic doc, employee
    // shows the 'PII incomplete' indicator until a user with PII access
    // fills it in.
    const employeeData = {
      role: "employee",
      permissions: [],
      employeeId: finalEmployeeId,
      personalInfo: {
        ...formData.personalInfo,
        fullName: `${formData.personalInfo?.firstName || ""} ${formData.personalInfo?.middleName || ""} ${formData.personalInfo?.lastName || ""}`.replace(/\s+/g, ' ').trim()
      },
      employment: {
        department: formData.employment.department || "",
        startDate: formData.employment.startDate,
        employmentType: formData.employment.employmentType || "regular",
        inBankExportFile: formData.employment.inBankExportFile !== undefined
          ? formData.employment.inBankExportFile : true,
        workSchedule: formData.employment.workSchedule || {},
        // M4: pay-basis toggle lives on the PARENT doc (a pay-policy flag,
        // not PII) so the server eligibility/compute path can read it.
        payBasis: formData.employment.payBasis || "fixed",
        // Contributions on parent doc per PRD §5.2.
        ...contributions,
        // Salary fields only included when caller has PII access; service
        // routes these to the PII subcollection's compensation block.
        ...piiCompensation,
      },
      // banking + governmentIds only when caller has PII access.
      ...(hasSensitiveAccess && {
        governmentIds: formData.governmentIds,
        banking: formData.banking,
      }),
      metadata: {
        isDeleted: false,
        version: 1,
        createdBy: user.userData?.userDocId || user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: user.userData?.userDocId || user.uid,
      },
    };

    // 4. Generate doc ID + write via service. The service splits employeeData
    // into basic + PII docs and writes both atomically with the audit entry.
    const docId = doc(collection(db, "temp")).id;
    const auditEntry = {
      action: "create",
      userId: user.userData?.userDocId || user.uid,
      userEmail: user.email || "",
      userName: user.displayName || user.email || "",
      changes: {},
      metadata: {
        userAgent: navigator.userAgent,
        ipAddress: "",
      },
    };

    await createEmployee(company.id, docId, employeeData, auditEntry);
  };

  /**
   * Handle updating an existing employee
   */
  const handleUpdateEmployee = async () => {
    // Find the existing employee document
    const employeesRef = collection(db, "companies", company.id, "employees");
    const q = query(
      employeesRef,
      where("role", "==", "employee"),
      where("employeeId", "==", currentEmployeeId)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      throw new Error("Employee not found");
    }

    const docId = snapshot.docs[0].id;
    const existingData = snapshot.docs[0].data();

    // Save-layer guard: never persist an end date earlier than the start date.
    // The employment step validates this too, but enforcing it here closes any
    // path that bypasses step validation (e.g. saving from another step).
    if (
      formData.employment?.endDate &&
      formData.employment?.startDate &&
      new Date(formData.employment.endDate) <
        new Date(formData.employment.startDate)
    ) {
      throw new Error("End date cannot be before start date");
    }

    // Normalize compensation values for comparison + write.
    // Contributions are written to parent doc regardless of PII access.
    // Salary fields are only written when the editor has emp:pii:edit;
    // omitting them means saveEmployee won't attempt the PII subcollection
    // write at all, which would otherwise fail the rule and break the
    // entire batch.
    const contributions = {
      sssContribution: Math.round((parseFloat(formData.employment.sssContribution) || 0) * 100) / 100,
      pagibigContribution: Math.round((parseFloat(formData.employment.pagibigContribution) || 0) * 100) / 100,
      philhealthContribution: Math.round((parseFloat(formData.employment.philhealthContribution) || 0) * 100) / 100,
      withholdingTax: Math.round((parseFloat(formData.employment.withholdingTax) || 0) * 100) / 100,
    };
    const piiCompensation = hasSensitiveAccess
      ? {
          salaryType: formData.employment.salaryType || 'daily',
          dailySalary: Math.round((parseFloat(formData.employment.dailySalary) || 0) * 100) / 100,
          monthlySalary: Math.round((parseFloat(formData.employment.monthlySalary) || 0) * 100) / 100,
          // PRD_CbaPremiumBase C3 — rides the SAME PII-compensation path as the
          // salary it re-bases. `=== true` normalizes to a real boolean: this
          // object reaches Firestore, and an `undefined` would throw (the
          // project does not set ignoreUndefinedProperties).
          cbaPremiumBase: formData.employment.cbaPremiumBase === true,
        }
      : {};

    // Empty end date normalizes to null — UAT carries all three of null, "" and
    // absent for "no end date", and every reader treats them alike.
    const endDateValue = formData.employment.endDate || null;

    // Build the flat update shape using DOTTED PATHS for employment fields.
    // Object-replacement of the `employment` map would destroy any legacy
    // fields not listed (compensationHistory, scheduledChanges, salary fields
    // for basic-only callers, etc.). Dotted paths are surgical — Firestore
    // update() only touches the listed nested fields. PRD §5.2.
    const updateData = {
      // personalInfo replaced as a whole — no PII subset within it.
      personalInfo: {
        ...formData.personalInfo,
        fullName: `${formData.personalInfo?.firstName || ""} ${formData.personalInfo?.middleName || ""} ${formData.personalInfo?.lastName || ""}`.replace(/\s+/g, ' ').trim()
      },

      // Employment fields — dotted paths preserve unlisted fields on the
      // parent doc (legacy salary, compensationHistory, etc.) untouched.
      "employment.department": formData.employment.department,
      "employment.employmentType": formData.employment.employmentType,
      "employment.inBankExportFile": formData.employment.inBankExportFile,
      // M4: parent-doc pay-basis toggle (not PII).
      "employment.payBasis": formData.employment.payBasis || "fixed",
      "employment.workSchedule": formData.employment.workSchedule,
      "employment.startDate": formData.employment.startDate,
      // BL-075 — write the form's value straight through; do NOT fall back to
      // existingData. The form is seeded from the stored doc on load, so that
      // fallback could only ever fire when the user deliberately CLEARED a field,
      // where it silently restored the old value and made clearing an end date
      // impossible (the UI greyed out Reason and hid Notes, so it looked like it
      // had worked). Un-ending an employee needed a Firebase console edit.
      //
      // Reason and notes clear WITH the date, however the date was emptied: both
      // are meaningless without it, and the form hides both once it's gone (the
      // Reason select disables, the Notes row unmounts), so anything left behind
      // is invisible-but-persisted.
      "employment.endDate": endDateValue,
      "employment.endReason": endDateValue ? formData.employment.endReason || null : null,
      "employment.notes": endDateValue ? formData.employment.notes || null : null,

      // Contributions on parent doc (always written).
      "employment.sssContribution": contributions.sssContribution,
      "employment.philhealthContribution": contributions.philhealthContribution,
      "employment.pagibigContribution": contributions.pagibigContribution,
      "employment.withholdingTax": contributions.withholdingTax,

      // Salary fields only when caller has PII access. splitEmployeeData
      // routes "employment.salaryType" / dailySalary / monthlySalary to
      // the PII subcollection's compensation block. For basic-only callers,
      // omitting these means the parent doc's existing salary fields (if
      // any — legacy employees) stay untouched.
      ...(hasSensitiveAccess && {
        "employment.salaryType": piiCompensation.salaryType,
        "employment.dailySalary": piiCompensation.dailySalary,
        "employment.monthlySalary": piiCompensation.monthlySalary,
        "employment.cbaPremiumBase": piiCompensation.cbaPremiumBase,
      }),

      // Top-level PII keys — whole object to the PII subcollection.
      // Only included when caller has PII access; otherwise the parent
      // doc's legacy banking/governmentIds (if any) are left as-is.
      ...(hasSensitiveAccess && {
        governmentIds: formData.governmentIds,
        banking: formData.banking,
      }),

      "metadata.updatedAt": serverTimestamp(),
      "metadata.updatedBy": user.userData?.userDocId || user.uid,
      "metadata.version": (existingData.metadata?.version || 1) + 1,
    };

    // No status flip on an end date: Active/Inactive is derived from
    // employment.startDate/endDate everywhere it is displayed, so setting an end
    // date already reads as Inactive the moment it is saved (C1a).

    // Build audit entry only if there are real changes to record.
    const allChanges = employeeAuditService.compareObjects(existingData, updateData);
    const auditEntry = Object.keys(allChanges).length > 0
      ? {
          action: "update",
          userId: user.userData?.userDocId || user.uid,
          userEmail: user.email || "",
          userName: user.displayName || user.email || "",
          changes: allChanges,
          metadata: {
            userAgent: navigator.userAgent,
            ipAddress: "",
          },
        }
      : undefined;

    await saveEmployee(company.id, docId, updateData, auditEntry);
  };

  // Prevent access if no auth or company
  if (authLoading || loading) {
    return (
      <div className="employee-loading">
        <div className="employee-spinner"></div>
      </div>
    );
  }

  if (!user || !company) {
    navigate("/");
    return null;
  }

  // Progress bar width calculation
  const getProgressWidth = () => {
    return `${(currentStep / totalSteps) * 100}%`;
  };

  // Render the appropriate step component. Maps the current step index
  // (1-based) to the corresponding step key in `wizardSteps`, then to a
  // component. The Sensitive Info entry is conditionally present based on
  // `hasSensitiveAccess` — the wizard collapses to 3 steps without it.
  const renderStep = () => {
    const commonProps = {
      formData,
      handleInputChange,
      errors,
      setErrors,
    };

    const stepKey = wizardSteps[currentStep - 1];

    switch (stepKey) {
      case "personal":
        return (
          <StepPersonalInfo
            {...commonProps}
            handleNext={handleNext}
            handleCancel={handleCancel}
            employeeIdType={employeeIdType}
            setEmployeeIdType={setEmployeeIdType}
            nextEmployeeId={nextEmployeeId}
            checkEmployeeIdAvailable={checkEmployeeIdAvailable}
            isEdit={isEdit}
            nextStepLabel={hasSensitiveAccess ? "Next: Sensitive Info →" : "Next: Employment →"}
          />
        );
      case "employment":
        return (
          <StepEmployment
            {...commonProps}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
            isEdit={isEdit}
            employeeDocId={employeeDocId}
            refreshData={loadEmployeeData}
            scheduleDefaults={scheduleDefaults}
            nextStepLabel="Next: Review →"
            onClearEndDate={() => setShowClearEndDateConfirm(true)}
          />
        );
      case "sensitive":
        return (
          <StepSensitiveInfo
            {...commonProps}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
            companyMinimumDailyWage={companyMinimumDailyWage}
          />
        );
      case "review":
        return (
          <StepReview
            formData={formData}
            hasSensitiveAccess={hasSensitiveAccess}
            saving={saving}
            handleSave={handleSubmit}
            handlePrevious={handlePrevious}
            basisAgencies={basisAgencies}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="add-employee-container">
      {/* Page Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        margin: "0 0 0.75rem 0",
      }}>
        <h1 style={{
          fontSize: "1.25rem",
          fontWeight: "700",
          color: "var(--ui-primary)",
          margin: 0,
        }}>
          {isEdit ? "Edit Employee" : "Add New Employee"}
        </h1>
        {isEdit && (
          <button
            type="button"
            onClick={() => setShowPinModal(true)}
            className="btn btn--secondary"
            style={{ padding: "8px 16px", whiteSpace: "nowrap" }}
          >
            Reset Kiosk PIN
          </button>
        )}
      </div>

      {/* Progress Stepper with Blue-to-Gold Gradient */}
      <div className="stepper-container">
        {/* Progress Bar */}
        <div className="progress-bar">
          <div
            className={`progress-bar-fill progress-bar-fill--step-${currentStep}`}
            style={{ width: getProgressWidth() }}
          ></div>
        </div>

        <div className="stepper">
          {wizardSteps.map((stepKey, idx) => {
            const stepNum = idx + 1;
            const isSensitive = stepKey === "sensitive";
            return (
              <div
                key={stepKey}
                className={`stepper-step ${
                  currentStep >= stepNum ? "stepper-step--active" : ""
                } ${currentStep > stepNum ? "stepper-step--completed" : ""}`}
              >
                <div className="stepper-step__indicator">{stepNum}</div>
                <div className="stepper-step__label">
                  {stepLabels[stepKey]}
                  {isSensitive && (
                    <span style={{ marginLeft: "4px" }}><Icon name="lock" size={13} /></span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="add-employee-content">{renderStep()}</div>

      {/* Error Display */}
      {errors.submit && (
        <div className="add-employee-error-message">{errors.submit}</div>
      )}

      <ConfirmModal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={confirmDiscardAndLeave}
        title="Discard unsaved changes?"
        message="Your employee data hasn't been saved. Continue without saving?"
        confirmText="Discard"
        cancelText="Keep editing"
        variant="warning"
      />

      {/* BL-075 — clearing an end date puts the employee back on the billable seat
          count immediately (billing derives from the employment dates). Name that
          here rather than let it surface on the next invoice. */}
      <ConfirmModal
        isOpen={showClearEndDateConfirm}
        onClose={() => setShowClearEndDateConfirm(false)}
        onConfirm={clearEndDate}
        title="Clear end date?"
        message={`${
          `${formData.personalInfo?.firstName || ""} ${formData.personalInfo?.lastName || ""}`.trim() ||
          "This employee"
        } goes back to being currently employed.`}
        confirmText="Yes, clear it"
        cancelText="Cancel"
        variant="warning"
      >
        <ul className="clear-end-date-effects">
          <li>The end date, reason and notes are removed</li>
          <li>They count as a <strong>billable seat</strong> again</li>
        </ul>
        <p className="clear-end-date-note">
          Nothing is saved until you submit the form.
        </p>
      </ConfirmModal>

      {showPinModal && (
        <ResetKioskPinModal
          employeeId={formData.employeeId}
          employeeName={`${formData.personalInfo?.firstName || ""} ${formData.personalInfo?.lastName || ""}`.trim()}
          onClose={() => setShowPinModal(false)}
        />
      )}
    </div>
  );
};
